import type { AxiosError } from 'axios'

export type ApiErrorPayload = unknown

export class ApiError extends Error {
    name = 'ApiError' as const

    // explicit raw error reference (AxiosError / etc)
    originalError?: unknown
    // original message (useful when friendly message is generic)
    debugMessage?: string

    status?: number
    code?: string
    method?: string
    url?: string
    requestId?: string

    data?: ApiErrorPayload

    isNetworkError?: boolean
    isTimeout?: boolean
    isCanceled?: boolean

    constructor(message: string, init?: Partial<ApiError>) {
        super(message)
        Object.assign(this, init)
    }
}

function pickRequestId(headers: unknown): string | undefined {
    const h = (headers ?? {}) as Record<string, any>
    return (
        h['x-request-id'] ??
        h['x-correlation-id'] ??
        h['request-id'] ??
        h['traceparent']
    )
}

export function toApiError(err: unknown): ApiError {
    if (err instanceof ApiError) return err

    const ax = err as AxiosError

    const isAxios =
        typeof ax === 'object' &&
        ax !== null &&
        'isAxiosError' in ax &&
        (ax as any).isAxiosError === true

    if (!isAxios) {
        const message = err instanceof Error ? err.message : 'Unknown error'
        return new ApiError(message, {
            originalError: err,
            debugMessage: err instanceof Error ? err.message : undefined,
        })
    }

    const status = ax.response?.status
    const method = ax.config?.method?.toUpperCase()
    const url = ax.config?.url
    const code = ax.code
    const data = ax.response?.data
    const requestId = pickRequestId(ax.response?.headers)

    const isCanceled = code === 'ERR_CANCELED'
    const isTimeout = code === 'ECONNABORTED' || code === 'ETIMEDOUT'
    const isNetworkError =
        !ax.response && (code === 'ERR_NETWORK' || ax.message?.toLowerCase().includes('network'))

    const friendly =
        status === 401
        ? 'You’re not signed in or your session expired.'
        : status === 403
            ? 'You don’t have permission to do that.'
            : status === 404
            ? 'We couldn’t find what you asked for.'
            : status && status >= 500
                ? 'The server had a problem. Please try again.'
                : isTimeout
                ? 'That took too long. Please try again.'
                : isNetworkError
                    ? 'Network error. Check your connection and try again.'
                    : ax.message || 'Request failed.'

    return new ApiError(friendly, {
        status,
        method,
        url,
        code,
        requestId,
        data,
        isCanceled,
        isTimeout,
        isNetworkError,

        originalError: err,
        debugMessage: ax.message,
    })
}
